import type { JSONContent } from "@tiptap/core";
import type { ScheduleMailParameterDefinition } from "./scheduleMailTemplate";

/**
 * 既知トークンだけに一致する正規表現。(?![a-z_]) でトークン名の途中一致（例: @start_datexxx）を防ぐ。
 *
 * エディタのチップ化とプレビューの置換で共用する。境界のルールが2箇所に分かれると
 * 片方だけ直す事故が起きる。backend の `ScheduleTemplateParameters._TOKEN_PATTERN` と対応する。
 */
export function buildTokenPattern(
  parameters: ScheduleMailParameterDefinition[],
): RegExp {
  return new RegExp(
    `@(${parameters.map((p) => p.name).join("|")})(?![a-z_])`,
    "g",
  );
}

function lineToNodes(line: string, tokenPattern: RegExp): JSONContent[] {
  const nodes: JSONContent[] = [];
  let lastIndex = 0;
  for (const match of line.matchAll(tokenPattern)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      nodes.push({ type: "text", text: line.slice(lastIndex, index) });
    }
    nodes.push({ type: "mention", attrs: { id: match[1], label: match[1] } });
    lastIndex = index + match[0].length;
  }
  if (lastIndex < line.length) {
    nodes.push({ type: "text", text: line.slice(lastIndex) });
  }
  return nodes;
}

export function textToDocJSON(
  text: string,
  parameters: ScheduleMailParameterDefinition[],
): JSONContent {
  const tokenPattern = buildTokenPattern(parameters);
  const paragraphs = text.split("\n").map((line) => {
    const content = lineToNodes(line, tokenPattern);
    return content.length > 0
      ? { type: "paragraph", content }
      : { type: "paragraph" };
  });
  return { type: "doc", content: paragraphs };
}

export function docJSONToText(
  doc: JSONContent,
  parameters: ScheduleMailParameterDefinition[],
): string {
  const knownTokenNames = new Set(parameters.map((p) => p.name));
  const lines = (doc.content ?? []).map((paragraph) =>
    (paragraph.content ?? [])
      .map((node) => {
        if (node.type === "mention") {
          const id = String(node.attrs?.id ?? "");
          return knownTokenNames.has(id) ? `@${id}` : "";
        }
        return node.text ?? "";
      })
      .join(""),
  );
  return lines.join("\n");
}
